import { FC, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	ContentLayout,
	FormField,
	Header,
	Input,
	Modal,
	SpaceBetween,
	Table,
	TableProps
} from '@cloudscape-design/components'
import { useOktaAuth } from '@okta/okta-react'
import { colorBackgroundDropdownItemHover } from '@cloudscape-design/design-tokens'

import type { TagDetailOption } from '../../api/tags/getTagDetails'
import hasRequiredPermissions from '../../helper/hasRequiredPermissions'
import usePlatformOptionsMutations from '../../hooks/mutate/usePlatformOptionsMutations'
import usePlatformOptions from '../../hooks/query/usePlatformOptions'
import useUserPermissions from '../../hooks/query/useUserPermissions'
import styles from './index.module.css'

type Dialog = 'add' | 'edit' | 'delete' | null

const PlatformNames: FC = () => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string
	const { data: access } = useUserPermissions(thisUserEmail)
	const canUpdate = hasRequiredPermissions(
		['tag-details:*:update'],
		access?.permissions ?? []
	)
	const canDelete = hasRequiredPermissions(
		['tag-details:*:delete'],
		access?.permissions ?? []
	)

	const { data: platforms = [], isLoading, isError } = usePlatformOptions()
	const { create, remove, rename } = usePlatformOptionsMutations()

	const [query, setQuery] = useState('')
	const [dialog, setDialog] = useState<Dialog>(null)
	const [target, setTarget] = useState<TagDetailOption | null>(null)
	const [draft, setDraft] = useState('')
	const [error, setError] = useState('')
	const [notice, setNotice] = useState('')

	const busy = create.isLoading || rename.isLoading || remove.isLoading

	const items = useMemo(() => {
		const search = query.trim().toLowerCase()
		return [...platforms]
			.filter((p) => p.label.toLowerCase().includes(search))
			.sort((a, b) => a.label.localeCompare(b.label))
	}, [platforms, query])

	const open = (kind: Exclude<Dialog, null>, platform: TagDetailOption | null) => {
		setDialog(kind)
		setTarget(platform)
		setDraft(kind === 'edit' && platform ? platform.label : '')
		setError('')
	}
	const close = () => setDialog(null)

	const save = async () => {
		const name = draft.trim()
		if (!name) return setError('Enter a platform name.')
		if (dialog === 'edit' && target?.label === name) return close()

		const isDuplicate = platforms.some(
			(p) =>
				p.value !== (dialog === 'edit' ? target?.value : undefined) &&
				p.label.toLowerCase() === name.toLowerCase()
		)
		if (isDuplicate) return setError('A platform with this name already exists.')

		try {
			const next = { label: name, value: name }
			if (dialog === 'edit' && target) {
				await rename.mutateAsync({ current: target, next })
				setNotice(`Renamed "${target.label}" to "${name}".`)
			} else {
				await create.mutateAsync(next)
				setNotice(`Added "${name}".`)
			}
			close()
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Could not save. Try again.')
		}
	}

	const confirmDelete = async () => {
		if (!target) return
		try {
			await remove.mutateAsync(target)
			setNotice(`Deleted "${target.label}".`)
			close()
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Could not delete. Try again.')
		}
	}

	const columnDefinitions: TableProps.ColumnDefinition<TagDetailOption>[] = [
		{
			id: 'name',
			header: 'Platform name',
			cell: (item) => item.label,
			sortingField: 'label'
		},
		...(canUpdate || canDelete
			? [
					{
						id: 'actions',
						header: 'Actions',
						width: 120,
						cell: (item: TagDetailOption) => (
							<SpaceBetween direction="horizontal" size="xxs">
								{canUpdate && (<span title={`Edit "${item.label}"`}>
										<Button
											variant="inline-icon"
											iconName="edit"
											ariaLabel={`Edit ${item.label}`}
											disabled={busy}
											onClick={() => open('edit', item)}
										/>
									</span>
								)}
								{canDelete && (
									<span title={`Delete "${item.label}"`}>
										<Button
											variant="inline-icon"
											iconName="remove"
											ariaLabel={`Delete ${item.label}`}
											disabled={busy}
											onClick={() => open('delete', item)}
										/>
									</span>
								)}
							</SpaceBetween>
						)
					}
			  ]
			: [])
	]

	return (
		<ContentLayout
			header={
				<Header
					variant="h1"
					description="Platforms available in the Platform Name dropdown of the tag forms."
				>
					Platform Names
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="m">
				{notice && (
					<Alert type="success" dismissible onDismiss={() => setNotice('')}>
						{notice}
					</Alert>
				)}
				{isError && (
					<Alert type="error">Unable to load the platform list.</Alert>
				)}
				<div
					className={styles.table}
					style={
						{
							'--platform-row-hover': colorBackgroundDropdownItemHover
						} as React.CSSProperties
					}
				>
				<Table
					variant="container"
					trackBy="value"
					items={items}
					columnDefinitions={columnDefinitions}
					loading={isLoading}
					loadingText="Loading platforms"
					header={
						<Header
							counter={`(${items.length})`}
							actions={
								canUpdate && (
									<Button
										variant="primary"
										iconName="add-plus"
										disabled={busy}
										onClick={() => open('add', null)}
									>
										Add platform
									</Button>
								)
							}
						>
							Platforms
						</Header>
					}
					filter={
						<Input
							type="search"
							value={query}
							placeholder="Search platforms"
							onChange={({ detail }) => setQuery(detail.value)}
						/>
					}
					empty={
						<Box textAlign="center" color="inherit">
							{query ? `No platforms match "${query}".` : 'No platforms.'}
						</Box>
					}
				/>
				</div>
			</SpaceBetween>

			<Modal
				visible={dialog !== null}
				onDismiss={({ detail }) => {
					// Never close on outside click; lock the X and Esc while a request is in flight.
					if (detail.reason === 'overlay' || busy) return
					close()
				}}
				header={
					dialog === 'add'
						? 'Add platform'
						: dialog === 'edit'
						? `Rename "${target?.label}"`
						: `Delete "${target?.label}"`
				}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={close} disabled={busy}>
								Cancel
							</Button>
							<Button
								variant="primary"
								loading={busy}
								onClick={dialog === 'delete' ? confirmDelete : save}
							>
								{dialog === 'delete' ? 'Delete' : 'Save'}
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				{dialog === 'delete' ? (
					<SpaceBetween direction="vertical" size="s">
						<Box>
							Delete "{target?.label}" from the platform list? Existing tags
							keep this name.
						</Box>
						{error && <Alert type="error">{error}</Alert>}
					</SpaceBetween>
				) : (
					<FormField label="Platform name" errorText={error}>
						<Input
							autoFocus
							value={draft}
							disabled={busy}
							placeholder={dialog === 'add' ? 'New platform name' : undefined}
							onChange={({ detail }) => setDraft(detail.value)}
							onKeyDown={({ detail }) => detail.key === 'Enter' && save()}
						/>
					</FormField>
				)}
			</Modal>
		</ContentLayout>
	)
}

export default PlatformNames